// Users management tab in Settings
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { UserPlus, Users, Key, Trash2, Edit, AlertCircle, Shield, Eye, FileEdit } from 'lucide-react'
import { useUsers } from '@/hooks/useUsers'
import { UserDialog } from './UserDialog'
import { PasswordResetDialog } from './PasswordResetDialog'
import type { User } from '@/types/user'

export function UsersSettingsTab() {
  const { users, isLoading, error, deleteUser } = useUsers()
  const [userDialogOpen, setUserDialogOpen] = useState(false)
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const handleCreateUser = () => {
    setSelectedUser(null)
    setUserDialogOpen(true)
  }

  const handleEditUser = (user: User) => {
    setSelectedUser(user)
    setUserDialogOpen(true)
  }

  const handleResetPassword = (user: User) => {
    setSelectedUser(user)
    setPasswordDialogOpen(true)
  }

  const handleDeleteUser = async (user: User) => {
    if (deleteConfirm !== user.id) {
      setDeleteConfirm(user.id)
      setTimeout(() => setDeleteConfirm(null), 3000)
      return
    }

    try {
      await deleteUser(user.id)
      setDeleteConfirm(null)
    } catch (err) {
      alert('Failed to delete user: ' + (err as Error).message)
    }
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'system_admin':
        return <Shield className="h-3 w-3" />
      case 'org_admin':
        return <Shield className="h-3 w-3" />
      case 'analyst':
        return <FileEdit className="h-3 w-3" />
      case 'viewer':
        return <Eye className="h-3 w-3" />
      default:
        return null
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'system_admin':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'org_admin':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'analyst':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'viewer':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const formatRole = (role: string) => {
    return role.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kaycoo-teal-dark mx-auto mb-4"></div>
          <p className="text-sm text-gray-500">Loading users...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage user accounts, roles, and permissions
          </p>
        </div>
        <Button
          onClick={handleCreateUser}
          className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Create User
        </Button>
      </div>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" />
            Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <Alert>
              <AlertDescription className="text-sm">
                No users yet. Create your first user to get started.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center justify-between rounded-lg border p-4 ${
                    !user.is_active ? 'bg-gray-50 opacity-60' : 'bg-white'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium text-gray-900">
                          {user.full_name || user.email.split('@')[0]}
                        </p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <Badge
                        variant="outline"
                        className={`${getRoleBadgeColor(user.role)} gap-1`}
                      >
                        {getRoleIcon(user.role)}
                        {formatRole(user.role)}
                      </Badge>
                      {user.has_all_customer_access && (
                        <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                          All Customers
                        </Badge>
                      )}
                      {!user.is_active && (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    {user.last_login && (
                      <p className="text-xs text-gray-400 mt-1">
                        Last login: {new Date(user.last_login).toLocaleString()}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditUser(user)}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResetPassword(user)}
                    >
                      <Key className="h-3 w-3 mr-1" />
                      Reset Password
                    </Button>
                    <Button
                      size="sm"
                      variant={deleteConfirm === user.id ? 'destructive' : 'outline'}
                      onClick={() => handleDeleteUser(user)}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      {deleteConfirm === user.id ? 'Confirm Delete' : 'Delete'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <UserDialog
        open={userDialogOpen}
        onOpenChange={setUserDialogOpen}
        user={selectedUser}
      />
      <PasswordResetDialog
        open={passwordDialogOpen}
        onOpenChange={setPasswordDialogOpen}
        user={selectedUser}
      />
    </div>
  )
}
